import { useEffect, useState } from "react";
import { FoundPhysicalAddressesEntry, knxReadBack } from "./KnxReadback";
import KnxBridge, { DeviceNotReachable, knxbridgeEmitter, refreshUI } from "../KnxBridge";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import React from "react";
import ReadBack from "./ReadBack";
import BaseDialog, { Field, FieldSet } from "../modelDialogs/BaseDialog";
import { itx } from "../translationItx/itx";
import { openProgrammingPane } from "../components/Menubar";
import ReadBackViaKnxBridge from "./ReadBackViaKnxBridge";
import { gatewayIp, bridgeOn } from "../KnxBridge";
import { updateToastProgress } from "../components/Toasts";
import BridgeStatus from "./BridgeStatus";

export enum readMode {
  groupMonitor = 0,
  deviceInfo = 1,
  programmingMode = 2,
  individualAddressCheck = 3,
  linescan = 4,
  areascan = 5, // Not in use currently
}

export default function GroupMonitorDialog2(props: any) {
  const {} = props;
  const [gatewayIP, setGatewayIP] = useState(gatewayIp);
  const [bridgeOnline, setBridgeOnline] = useState(bridgeOn);
  const [listMode, setListMode] = useState(readMode.groupMonitor);
  const [scanning, setScanning] = useState(false);
  const [diagnosticRunning, setDiagnosticRunning] = useState(false);
  const [scannedlist, setScannedList] = useState<Array<FoundPhysicalAddressesEntry>>([]);
  const [inputline, setInputLine] = useState("");
  const [currentSegment, setCurrentSegment] = useState(0);

  const rb = knxReadBack();

  const windowWidth = window.innerWidth - 200 || 900;

  useEffect(() => {
    // triggers rerender only when IP changes ; consumes less cpu resources than update via ping
    const ipHandler = (newIp: string) => {
      setGatewayIP(newIp);
    };
    const bridgeHandler = (status: boolean) => {
      setBridgeOnline(status);
    };
    knxbridgeEmitter.on("ipChange", ipHandler);
    knxbridgeEmitter.on("bridgeChange", bridgeHandler);

    rb.readbackMonitoringIsRunning = true;
    openProgrammingPane();
    KnxBridge.start();
    return () => {
      knxbridgeEmitter.off("ipChange", ipHandler); // clean up
      knxbridgeEmitter.off("bridgeChange", bridgeHandler);
      KnxBridge.stopGroupMonitor();
    };
  }, []);

  useOnChangeWatcher(rb.watchModified);

  const addressInput = React.createRef<HTMLInputElement>();
  const areaLineInput = React.createRef<HTMLInputElement>();

  const alreadyImportedDevices = new Array<string>();
  state.allDevices().forEach((d) => alreadyImportedDevices.push(d.physicalAddress));

  let sortedLineScannedAddresses = rb.lineScannedAddresses.sort((a, b) => {
    const [ax, ay, az] = a.addr.split(".").map(Number);
    const [bx, by, bz] = b.addr.split(".").map(Number);

    // compare x, then y and z in order
    if (ax !== bx) return ax - bx;
    if (ay !== by) return ay - by;
    return az - bz;
  });

  const telegramsDiscovered = rb.telegrams.map((item, index) => {
    return (
      <tr key={index + 1} style={{ backgroundColor: index % 2 === 0 ? "#f0f0f0" : "white", verticalAlign: "middle" }}>
        <td> {index + 1} </td>
        <td> {item.timestamp} </td>
        <td> {item.priority} </td>
        <td> {item.sourceAddr} </td>
        <td> {item.targetAddr} </td>
        <td> {item.hopcount} </td>
        <td> {item.medium} </td>
        <td> {item.device} </td>
        <td> {item.payload} </td>
        <td> {item.feedback} </td>
        <td> {item.systemBroadcast} </td>
        <td> {item.ackRequested} </td>
        <td> {item.isRepetition} </td>
        <td> {item.structLength} </td>
        <td> {item.type} </td>
        <td> {item.dpttype} </td>
        <td> {item.dptvalue} </td>
        <td> {item.apciType} </td>
        <td> {item.apdu} </td>
        <td> {item.asdu} </td>
        <td> {item.decodedAsdu} </td>
        <td> {item.decodedLteFrame} </td>
      </tr>
    );
  });

  const deviceElementsLineScanned = sortedLineScannedAddresses.map((item, index) => {
    return (
      <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#f0f0f0" : "white", verticalAlign: "middle" }}>
        <td style={{ verticalAlign: "middle" }} key={item.addr}>
          {" "}
          {item.addr}{" "}
        </td>
        <td style={{ verticalAlign: "middle" }}> {item.mask ? item.mask : ""} </td>
      </tr>
    );
  });

  function addrStringToInt(a: string): number {
    const ss = a.split(".");
    return (parseInt(ss[0]) * 16 + parseInt(ss[1])) * 16 + parseInt(ss[2]);
  }

  async function scanLine() {
    if (areaLineInput.current) {
      const arealine = areaLineInput.current.value.trim();
      if (KnxBridge.isAreaLineValid(arealine)) {
        setInputLine(arealine);
        setScanning(true);
        rb.clearScannedAddrs();
        const [area, line] = arealine.split(".");
        const result = await KnxBridge.scanLine(area, line);
        const deviceAddresses = result
          .trim()
          .split("\n")
          .map((line) => {
            const [addr, mask] = line.split(";");
            return {
              addr: addr.trim(),
              mask: mask?.trim() || null,
              time: Date.now(),
            };
          });

        rb.setScannedAddrList(deviceAddresses);
        setScannedList(deviceAddresses);

        // commenting sequential line scan (high time complexity)
        // for(let segment = 0; segment < 256; segment++){
        //   if(!scanning) break;
        //   setCurrentSegment(segment);
        //   const fullAddr = `${arealine}.${segment}`;
        //   const res = await KnxBridge.isDevicePresentInNetwork(fullAddr);
        //   if(res != DeviceNotReachable) rb.registerScannedAddr(fullAddr);
        // }
        setCurrentSegment(0);
        setScanning(false);
        rb.setToastText(``, `${itx("knxBridge.lineScanComplete", `Line Scan Completed!`)}`, false);
      } else {
        rb.setToastText(
          ``,
          `${itx("knxBridge.readBackInvalidLine", `Please enter area line in correct format (example: 1.1)`)}`,
          false,
        );
      }
    }
  }

  function LoaderRenderer() {
    return (
      <div style={{ textAlign: "center", gridColumn: "1/-1" }}>
        <i className={`las la-spinner la-spin la-2x`}></i>
      </div>
    );
  }

  function EmptyRowsRenderer() {
    return (
      <div style={{ textAlign: "center", width: "100%" }}>
        {state.translate("readback.noDevicesToDisplay", "------ No Device Address to display ------")}
      </div>
    );
  }

  function showBatchStatistics(
    validDeviceCount: number,
    totalDevices: number,
    errorDeviceList: Array<string>,
    operation: string,
  ): Promise<void> {
    const title = itx("knxBridge.batchStats", "Batch Device Operation Statistics").replace("Operation", operation);
    const pastTense =
      operation == "Create"
        ? "created" // no itx
        : operation == "Readback"
          ? "readback" // no itx
          : "operated"; // no itx

    return new Promise<void>((resolve, reject) => {
      state.dialogManager.openDialog(
        <BaseDialog
          title={title}
          abortCaption={itx("close", "Schließen")}
          noOkButton={true}
          onOk={() => {
            resolve();
            return true;
          }}
        >
          {
            <table style={{ textAlign: "left" }}>
              <tr>
                <td>
                  <b>{itx("knxBridge.status", "STATUS:")}</b>
                </td>
                <td>
                  <b>{itx("knxBridge.readbackOperationComplete", "Batch Operation Completed")}</b>
                </td>
              </tr>
              <tr>
                <td>{`Total devices ${pastTense}`}</td>
                <td>{`${validDeviceCount} ${validDeviceCount == totalDevices ? "(All)" : ""}`}</td>
              </tr>
              <tr>
                <td>{`Total devices not ${pastTense} (Operation not applicable)`}</td>
                <td>{`${totalDevices - validDeviceCount} ${validDeviceCount == 0 ? "(All)" : ""}`}</td>
              </tr>
              {totalDevices - validDeviceCount > 0 && (
                <tr>
                  <td>{`List of device addresses not ${pastTense}:`}</td>
                  <td>{errorDeviceList.join(", ")}</td>
                </tr>
              )}
            </table>
          }
        </BaseDialog>,
        true,
      );
    });
  }

  return (
    <BaseDialog
      className="groupMonitorDialog"
      title={itx("inlineButton.diagnostics", "Diagnostics")}
      noAbortButton={true}
      onOk={() => {
        state.dialogManager.closeDialog();
        return true;
      }}
      okCaption={state.translate("ok", "Ok")}
    >
      <div style={{ padding: "10px" }}>
        {/* {!gatewayIP && !bridgeOnline && <div>{itx("knxBridge.disconnectedFromNw", "Not connected to gateway.")} <a href="estada://bridgegateway">{itx("knxBridge.startEstadaGateWay", "Start Estada Gateway")}</a></div>} */}
        {!gatewayIP && !bridgeOnline && <BridgeStatus />}
        {!gatewayIP && bridgeOnline && (
          <div>{`${itx("knxBridge.gatewaydisconnectedNw", "⚠️ Gateway is active but not connected to the network.")} ${itx("knxBridge.restartGateway", "If it doesn't reconnect automatically, try restarting the gateway.")}`}</div>
        )}
        {gatewayIP && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div>
              {itx("knxBridge.connectGateewayIp", "Connected gateway IP:")}&nbsp;
              <b>{gatewayIP}</b>
            </div>
            <div>
              {itx("diagnostic.diagnoticMode", "Diagnotic Mode: ")}
              <b>{`${listMode == readMode.groupMonitor ? itx("diagnostic.groupMonitor", "Group Monitor") : itx("readback.linescan", "Line Scan")}`}</b>
              &nbsp;
              <span>
                <button
                  className={"button"}
                  disabled={scanning}
                  onClick={() => {
                    const mode = listMode == readMode.groupMonitor ? readMode.linescan : readMode.groupMonitor;
                    setListMode(mode);
                  }}
                >
                  {`${itx("readback.switchto", "Switch to ")} ${listMode == readMode.linescan ? itx("diagnostic.groupMonitor", "Group Monitor") : itx("readback.linescan", "Line Scan")}`}{" "}
                </button>
              </span>
            </div>

            {listMode == readMode.groupMonitor && (
              <div>
                {itx("diagnostic.telegrams", "Telegrams")}:
                <button
                  className={"button"}
                  disabled={diagnosticRunning}
                  onClick={async () => {
                    setDiagnosticRunning(true);
                    KnxBridge.startGroupMonitor();
                  }}
                >
                  {!diagnosticRunning ? itx("diagnostics.start", "Start") : itx("diagnostics.runnning", "Runnning")}
                  {diagnosticRunning && (
                    <span className="appTextSuffix rightAlign">
                      <i className={`las la-spinner la-spin`}></i>
                    </span>
                  )}
                </button>
                <button
                  className={"button"}
                  disabled={!diagnosticRunning}
                  onClick={() => {
                    setDiagnosticRunning(false);
                    KnxBridge.stopGroupMonitor();
                  }}
                >
                  {itx("diagnostics.stop", "Stop")}{" "}
                </button>
                <button
                  className={"button"}
                  onClick={() => {
                    rb.clearTelegrams();
                  }}
                >
                  {itx("diagnostics.clear", "Clear")}{" "}
                </button>
                {/* <LoaderRenderer /> */}
                <div
                  style={{
                    margin: "9px 5px",
                    height: "300px",
                    width: "900px",
                    overflow: "auto",
                    padding: "3px",
                    boxShadow: "0 0 0.25cm rgba(0, 0, 0, 0.5)",
                    border: "1px solid grey",
                  }}
                >
                  {telegramsDiscovered.length == 0 ? (
                    <EmptyRowsRenderer />
                  ) : (
                    <table style={{ width: `${windowWidth}px` }}>
                      <tr style={{ backgroundColor: "grey", verticalAlign: "middle" }}>
                        <th> {itx("groupMonitor.sno", "SNo")} </th>
                        <th> {itx("groupMonitor.timestamp", "Timestamp")} </th>
                        <th> {itx("groupMonitor.priority", "Priority")} </th>
                        <th> {itx("groupMonitor.sourceAddress", "Source Address")} </th>
                        <th> {itx("groupMonitor.targetAddr", "Target Address")} </th>
                        <th> {itx("groupMonitor.hopcount", "Hop Count")} </th>
                        <th> {itx("groupMonitor.medium", "Medium")} </th>
                        <th> {itx("groupMonitor.device", "Device")} </th>
                        <th> {itx("groupMonitor.payload", "Payload")} </th>
                        <th> {itx("groupMonitor.feedback", "Feedback")} </th>
                        <th> {itx("groupMonitor.systemBroadcast", "System Broadcast")} </th>
                        <th> {itx("groupMonitor.ackRequested", "Ack Requested")} </th>
                        <th> {itx("groupMonitor.isRepetition", "Repetition")} </th>
                        <th> {itx("groupMonitor.structLength", "Struct Length")} </th>
                        <th> {itx("groupMonitor.type", "Type")} </th>

                        <th> {itx("groupMonitor.dpttype", "Dpt type")} </th>
                        <th> {itx("groupMonitor.dptvalue", "Dpt value")} </th>
                        <th> {itx("groupMonitor.apciType", "ApciType")} </th>
                        <th> {itx("groupMonitor.apdu", "Apdu")} </th>
                        <th> {itx("groupMonitor.asdu", "Asdu")} </th>
                        <th> {itx("groupMonitor.decodedAsdu", "Decoded Asdu")} </th>
                        <th> {itx("groupMonitor.decodedLteFrame", "Decoded Lte Frame")} </th>
                      </tr>
                      {telegramsDiscovered}
                    </table>
                  )}
                </div>
              </div>
            )}

            {listMode == readMode.linescan && (
              <div>
                <div>
                  <span>{itx("readback.line", "Line")}</span>&nbsp;&nbsp;
                  <input
                    ref={areaLineInput}
                    id="line_value"
                    className="input"
                    maxLength={state.maxCharLimit.address}
                    defaultValue={""}
                    disabled={scanning}
                  />
                  <button
                    className={"button"}
                    disabled={scanning}
                    onClick={async () => {
                      await scanLine();
                    }}
                  >
                    {scanning ? itx("readback.scanning", "Scanning") : itx("readback.scan", "Scan")}
                    {scanning && (
                      <span className="appTextSuffix rightAlign">
                        <i className={`las la-spinner la-spin`}></i>
                      </span>
                    )}
                  </button>
                  <button
                    className={"button"}
                    disabled={!scanning}
                    onClick={() => {
                      setScanning(false);
                    }}
                  >
                    {itx("readback.stopscan", "Stop")}{" "}
                  </button>
                </div>
                {scanning && <div>{`${itx("readback.devicesInLine", "Active Devices in Line: ")} ${inputline}`}</div>}
                {/* (Scanning segment ${currentSegment}/255)... */}
                {/* <LoaderRenderer /> */}
                <div
                  style={{
                    margin: "9px 5px",
                    height: "300px",
                    overflow: "auto",
                    padding: "3px",
                    boxShadow: "0 0 0.25cm rgba(0, 0, 0, 0.5)",
                    border: "1px solid grey",
                  }}
                >
                  {deviceElementsLineScanned.length == 0 ? (
                    <EmptyRowsRenderer />
                  ) : (
                    <table style={{ width: "-webkit-fill-available" }}>{deviceElementsLineScanned}</table>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </BaseDialog>
  );
}
